import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getUserSurvey } from "../../api/api";
import SignedOutPrompt from "../SignedOutPrompt";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

const DEFAULT_THANKS_TITLE = "Thank you for your feedback";
const DEFAULT_THANKS_BODY =
  "We received your responses. We review all feedback and use it to improve Assembly Platform.";

/**
 * Post-submit thank-you screen. CTA returns to the beta survey list.
 *
 * @param {Object} props
 * @param {number} [props.userId]
 * @param {object} [props.user]
 * @returns {JSX.Element}
 */
export default function SurveyThanksPage({ userId, user }) {
  const { surveyId } = useParams();
  const [thanksTitle, setThanksTitle] = useState(DEFAULT_THANKS_TITLE);
  const [thanksBody, setThanksBody] = useState(DEFAULT_THANKS_BODY);
  const [loading, setLoading] = useState(Boolean(userId && surveyId));

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!userId || !surveyId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      const result = await getUserSurvey(userId, surveyId);
      if (!mounted) return;
      const thanks = result.data?.definition?.thanks;
      if (thanks?.title && thanks?.body) {
        setThanksTitle(thanks.title);
        setThanksBody(thanks.body);
      } else {
        setThanksTitle(DEFAULT_THANKS_TITLE);
        setThanksBody(DEFAULT_THANKS_BODY);
      }
      setLoading(false);
    }
    load();
    return () => {
      mounted = false;
    };
  }, [userId, surveyId]);

  if (!userId) {
    return (
      <SignedOutPrompt
        title="Beta feedback"
        description="Sign in to complete beta surveys and help shape Assembly."
      />
    );
  }

  if (user?.type === "organization") {
    return (
      <SignedOutPrompt
        variant="restricted"
        title="Beta feedback"
        description="Beta surveys are available on personal accounts."
      />
    );
  }

  return (
    <div className="bg-ui-background min-h-[calc(100dvh-3.5rem)] w-full">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:py-10">
        <Card className="border-ui-border">
          <CardHeader className="text-center">
            {loading ? (
              <p className="text-sm text-text-muted">Loading…</p>
            ) : (
              <>
                <CardTitle className="font-display text-2xl font-medium text-text-primary sm:text-3xl">
                  {thanksTitle}
                </CardTitle>
                <p className="mt-3 text-base text-text-secondary">{thanksBody}</p>
              </>
            )}
          </CardHeader>
          <CardContent className="flex justify-center">
            <Button asChild size="lg">
              <Link to="/beta">Back to surveys</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
